import { Box, CircularProgress, Grid } from '@material-ui/core'
import { withDetail } from 'components/domain/dashboard/GradeRelabelDialog'
import { useUser } from 'components/providers/UserProvider'
import { ReactComponent as PtStockIcon } from 'img/ptStock.svg'
import salesChannels from 'img/salesChannels.svg'
import { getGradeRelabelRows } from 'lib/api'
import { useInventoryStore } from 'lib/hooks/useInventoryStore'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { INVENTORY_STEPS } from 'shared'
import { SquareButton } from './SquareButton'

export const Welcome = (): JSX.Element => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const { user, setUser } = useUser()
  const history = useHistory()

  const setStep = useInventoryStore((state) => state.setStep)

  // A sync reads a listing on a grade we no longer offer as a card with no grade and asks for one: the stock moves
  // on PowerTools while the listing keeps the old grade, and the two stop matching. So the sync stays shut until
  // the seller has been through the relabel, which they reach from the announcement and deliberately not from here.
  // Only a seller with no date is asked, and the step waits for the answer: every sync starts from here.
  const [mustRelabel, setMustRelabel] = useState<boolean | undefined>(
    user?.ptInventory?.gradeRelabelCompletedAt ? false : undefined
  )

  useEffect(() => {
    if (mustRelabel !== undefined) return

    getGradeRelabelRows()
      .then(({ rows }) => {
        setMustRelabel(rows.length > 0)
        // Rails records the date for a seller with nothing to relabel, so the next visit does not ask again.
        if (rows.length === 0) {
          setUser({
            ...user,
            ptInventory: {
              ...user.ptInventory,
              gradeRelabelCompletedAt: new Date(),
              gradeRelabelCardsToFix: 0,
            },
          })
        }
      })
      .catch((error) => {
        // The sync is not held hostage to a check that could not run: it needs Rails too, and says so itself.
        console.error(error)
        enqueueSnackbar(withDetail(t('gradeRelabel.loadFailed'), error), { variant: 'error' })
        setMustRelabel(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isFirstImport = user?.ptInventory?.lastManualSyncWithCardmarketAt === undefined

  if (mustRelabel === undefined) {
    return (
      <div className="d-flex justify-content-center p-5">
        <CircularProgress />
      </div>
    )
  }

  const lockedClass = mustRelabel ? 'welcomeLocked' : undefined

  return (
    <>
      <Grid container>
        <Grid item lg={6}>
          <div className={lockedClass}>
            {/* When the user was force-redirected here for a graded-stock mismatch (forceGradeSync),
                explain why right above the "Sync your stock" title, in the page's normal body style.
                The flag (and so this note) is cleared once the user completes the sync. */}
            {user?.featureFlags?.forceGradeSync && (
              <div className="fw-bold warning-color bodyFont mb-4">
                {t('ptInventory.forceGradeSync.explanation')}
              </div>
            )}
            <Box className="title">
              <div className="titleFont">
                {isFirstImport
                  ? t('ptInventory.welcome.title.firstTimeImport')
                  : t('ptInventory.welcome.title.notFirstTimeImport')}
              </div>
            </Box>
            <Box marginY={5} />
            <Box marginY={2}>
              {isFirstImport ? (
                <div className="bodyFont">{t('ptInventory.welcome.text1')}</div>
              ) : (
                <div className="bodyFont">{t('ptInventory.welcome.text3')}</div>
              )}
            </Box>
            <Box marginY={5} />
            {isFirstImport && (
              <Box marginY={2}>
                <div className="bodyFont">{t('ptInventory.welcome.text2')}</div>
              </Box>
            )}
            <Box className="spacerBig" />
          </div>

          {mustRelabel && (
            <div className="fw-bold warning-color bodyFont mb-4" data-testid="inventory-locked">
              {t('gradeRelabel.inventoryLocked')}
            </div>
          )}
          <Grid container className={`gridContainer ${lockedClass ?? ''}`.trim()}>
            <Grid data-testid="import-stock-btn" item xs={12} sm={6} md={6} lg={6} xl={3}>
              <SquareButton
                // image={importStockIcon}
                icon="fa-regular fa-arrow-down-to-line"
                text={
                  isFirstImport
                    ? t('ptInventory.welcome.importStock.firstTimeImport')
                    : t('ptInventory.welcome.importStock.notFirstTimeImport')
                }
                type="primary"
                handleClick={() => setStep(INVENTORY_STEPS.CONNECT_ACCOUNT.idx)}
              />
            </Grid>
            {!isFirstImport && (
              <Grid data-testid="go-to-stock-btn" item xs={12} sm={6} md={6} lg={6} xl={3}>
                <SquareButton
                  image={<PtStockIcon />}
                  text={t('ptInventory.welcome.goToStock')}
                  type="secondary"
                  handleClick={() => history.push('/stock-pricing')}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item lg={6} className={lockedClass}>
          <Box display={{ xs: 'none', sm: 'none', md: 'none', lg: 'block' }}>
            <img src={salesChannels} width="100%" />
          </Box>
        </Grid>
      </Grid>
    </>
  )
}
